import type { FirestoreTimestamp } from './firestore'

/** Every email Faro sends is one of these; the copy lives in functions/src/utils/emailTemplates.ts. */
export type EmailTemplate = 'rename_notice' | 'lineup_announced' | 'set_times_out' | 'event_cancelled'

/**
 * The emails a user can turn off, one switch each. Templates outside
 * TEMPLATE_CATEGORY are service email (the rename notice): no unsubscribe link,
 * no setting.
 */
export type EmailCategory = 'lineup' | 'schedule' | 'cancelled'
export type EmailInterestStatus = 'going' | 'interested'

export const EMAIL_CATEGORIES: readonly EmailCategory[] = ['lineup', 'schedule', 'cancelled']

export const TEMPLATE_CATEGORY: Partial<Record<EmailTemplate, EmailCategory>> = {
  lineup_announced: 'lineup',
  set_times_out: 'schedule',
  event_cancelled: 'cancelled',
}

/**
 * Who each category is for — fixed, not a setting. Lineup and set times reach
 * `interested` too: many people only switch to going once they pick sets, so
 * that's exactly when an interested person needs to hear. A cancellation only
 * changes plans for whoever is going.
 */
export const EMAIL_AUDIENCE: Record<EmailCategory, EmailInterestStatus[]> = {
  lineup: ['going', 'interested'],
  schedule: ['going', 'interested'],
  cancelled: ['going'],
}

/** Whether the user gets `category` emails; on unless they turned it off. */
export function emailCategoryEnabled(
  prefs: { emailCategories?: Partial<Record<EmailCategory, boolean>> } | null | undefined,
  category: EmailCategory,
): boolean {
  return prefs?.emailCategories?.[category] !== false
}

/**
 * `users/{uid}/private/eventMilestonesSeen` — when the user first saw each
 * milestone in the app (eventId → epoch millis). A milestone email is skipped
 * for anyone who already saw what it would tell them.
 */
export type EventMilestonesSeen = Partial<Record<EmailCategory, Record<string, number>>>

/** What a milestone email says about its event, frozen when it is enqueued. */
export interface EmailEventParams {
  id: string
  name: string
  type?: 'festival' | 'party'
  startDate?: string
  endDate?: string
  lineupCount?: number
  /** Up to five top-tier artist names; empty when the lineup has no tiers. */
  headliners?: string[]
  /** The event's cover, when it's a public https URL a mail client can load. */
  imageUrl?: string
}

/** `sending` = claimed by a processEmailQueue run; a claim that never finishes ends `failed` (`interrupted`), never re-sent. */
export type EmailStatus = 'pending' | 'sending' | 'sent' | 'skipped' | 'failed'

/** `opted_out` / `already_seen`: category emails only, re-checked at send time since quota can delay a send by days. */
export type EmailSkipReason = 'no_user' | 'disabled' | 'no_email' | 'unverified' | 'opted_out' | 'already_seen'

/**
 * `emailQueue/{id}` — one email to one recipient, written by the Admin SDK only.
 * The id is deterministic per (template, recipient[, event]) so enqueueing twice
 * never sends twice. A user recipient is stored by uid, never by address: the
 * address is read from Firebase Auth when the email goes out.
 */
export interface EmailQueueItem {
  template: EmailTemplate
  /** The user to email. Exactly one of recipientId / toAddress is set. */
  recipientId?: string
  /** Admin test sends only — a literal address, no Auth lookup. */
  toAddress?: string
  /** Overrides the recipient's language (`en_US` / `es_CL`); test sends use it to preview each one. */
  locale?: string
  /** Milestone templates only. */
  event?: EmailEventParams
  /** Milestone templates only: the recipient's status when enqueued (picks the footer's "why you got this"). */
  interestStatus?: EmailInterestStatus
  status: EmailStatus
  skipReason?: EmailSkipReason
  /** `failed` only: the provider's status and message, or `interrupted`. */
  error?: string
  /** The provider's message id, for looking a delivery up in the Resend dashboard. */
  providerId?: string
  createdAt: FirestoreTimestamp
  claimedAt?: FirestoreTimestamp
  sentAt?: FirestoreTimestamp
}
